import { useState, useRef } from 'react'
import {
  CloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Table,
  IconButton,
  NativeSelect,
  Spinner,
  Badge,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiEdit2, FiTrash2, FiAlertCircle } from 'react-icons/fi'
import { useForm, useWatch } from 'react-hook-form'
import { useBusinesses, useCreateBusiness, useUpdateBusiness, useDeleteBusiness, useBusinessSuggest } from '../../hooks/useBusiness'
import type { SaveBusinessRequest, BusinessSummary } from '../../types/business'
import { useNavigate } from 'react-router-dom'

export function BusinessManager() {
  const cancelRef = useRef<HTMLButtonElement>(null!)

  const { data: businesses, isLoading } = useBusinesses()
  const deleteMutation = useDeleteBusiness()

  const [editingBusiness, setEditingBusiness] = useState<BusinessSummary | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<BusinessSummary | null>(null)

  const openCreate = () => {
    setEditingBusiness(null)
    setShowForm(true)
  }

  const openEdit = (business: BusinessSummary) => {
    setEditingBusiness(business)
    setShowForm(true)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toaster.create({ title: 'Business deleted', type: 'success', duration: 3000 })
      setDeleteTarget(null)
    } catch {
      toaster.create({ title: 'Failed to delete business', type: 'error', duration: 3000 })
    }
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" mb={4}>
        <Text fontSize="lg" fontWeight="semibold">Businesses</Text>
        <Button size="sm" colorPalette="brand" onClick={openCreate}><FiPlus />Add Business
                  </Button>
      </HStack>

      {isLoading && (
        <VStack py={6}>
          <Spinner size="md" color="accent" />
        </VStack>
      )}

      {!isLoading && businesses && businesses.length === 0 && (
        <Text color="gray.500" fontSize="sm">No businesses defined yet.</Text>
      )}

      {!isLoading && businesses && businesses.length > 0 && (
        <Table.Root size="sm">
          <Table.Header bg="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>Locations</Table.ColumnHeader>
              <Table.ColumnHeader w="80px"></Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {businesses.map(business => (
              <Table.Row key={business.id}>
                <Table.Cell>
                  <Text fontWeight="medium" fontSize="sm">{business.label}</Text>
                  {business.description && (
                    <Text fontSize="xs" color="gray.500" lineClamp={1}>{business.description}</Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {business.type && <Badge colorPalette="purple" fontSize="xs">{business.type}</Badge>}
                </Table.Cell>
                <Table.Cell textAlign='end'>
                  <Text fontSize="sm">{business.worksiteCount}</Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={1}>
                    <IconButton
                      aria-label="Edit business"
                      size="xs"
                      variant="ghost"
                      colorPalette="brand"
                      onClick={() => openEdit(business)}><FiEdit2 /></IconButton>
                    <IconButton
                      aria-label="Delete business"
                      size="xs"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => setDeleteTarget(business)}><FiTrash2 /></IconButton>
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <BusinessChainFormModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          business={editingBusiness}
        />
      )}

      {/* Delete confirmation */}
      <Dialog.Root
        open={!!deleteTarget}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setDeleteTarget(null);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Delete Business</Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>{deleteTarget?.label}</strong>?
                  Worksites in this business will be unlinked but not deleted.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
}

// Form modal for business create/edit (exported for reuse from BusinessDetailPage)
export function BusinessChainFormModal({
  isOpen, onClose, business,
}: {
  isOpen: boolean
  onClose: () => void
  business: { id: string; label: string; description?: string | null; type?: string | null } | null
}) {
  const navigate = useNavigate()
  const isEdit = !!business
  const [dismissed, setDismissed] = useState(false)

  const createMutation = useCreateBusiness()
  const updateMutation = useUpdateBusiness(business?.id ?? '')

  const { register, handleSubmit, control, formState: { isSubmitting } } = useForm<SaveBusinessRequest>({
    defaultValues: business ? {
      label: business.label,
      description: business.description ?? '',
      type: business.type ?? '',
    } : {},
  })

  const labelValue = useWatch({ control, name: 'label' }) ?? ''
  const { data: suggestions } = useBusinessSuggest(labelValue, {
    enabled: !isEdit && !dismissed && labelValue.length >= 2,
  })

  const showSuggestions = !isEdit && !dismissed && suggestions && suggestions.length > 0

  const onSubmit = async (data: SaveBusinessRequest) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data)
        toaster.create({ title: 'Business updated', type: 'success', duration: 3000 })
      } else {
        await createMutation.mutateAsync(data)
        toaster.create({ title: 'Business created', type: 'success', duration: 3000 })
      }
      onClose()
    } catch {
      toaster.create({ title: `Failed to ${isEdit ? 'update' : 'create'} business`, type: 'error', duration: 3000 })
    }
  }

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Header>{isEdit ? 'Edit Business' : 'Add Business'}</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
              <Dialog.Body>
                <VStack gap={3}>
                  <Field.Root required>
                    <Field.Label fontSize="sm">Business Name</Field.Label>
                    <Input size="sm" {...register('label', { required: true })} />
                  </Field.Root>

                  {showSuggestions && (
                    <Box w="100%" bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                      <HStack gap={2} mb={2}>
                        <FiAlertCircle color="var(--chakra-colors-orange-500)" />
                        <Text fontSize="xs" fontWeight="semibold" color="orange.700">
                          Similar businesses already exist
                        </Text>
                      </HStack>
                      <VStack gap={1} align="stretch">
                        {suggestions.map(s => (
                          <HStack
                            key={s.id}
                            p={2}
                            bg="white"
                            borderRadius="md"
                            cursor="pointer"
                            _hover={{ bg: 'accent.soft' }}
                            onClick={() => {
                              onClose()
                              navigate(`/dashboard/businesses/${s.id}`)
                            }}
                          >
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="medium">{s.label}</Text>
                              <HStack gap={2}>
                                {s.type && <Badge colorPalette="purple" fontSize="2xs">{s.type}</Badge>}
                                <Text fontSize="xs" color="gray.500">
                                  {s.worksiteCount} location{s.worksiteCount !== 1 ? 's' : ''}
                                </Text>
                                <Text fontSize="xs" color="gray.400">
                                  {Math.round(s.similarity * 100)}% match
                                </Text>
                              </HStack>
                            </Box>
                          </HStack>
                        ))}
                      </VStack>
                      <Button
                        size="xs"
                        variant="ghost"
                        mt={2}
                        onClick={() => setDismissed(true)}
                        color="gray.500"
                      >
                        None of these — create new
                      </Button>
                    </Box>
                  )}

                  <Field.Root>
                    <Field.Label fontSize="sm">Type</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field placeholder="Select type" {...register('type')}>
                        <option value="Restaurant">Restaurant</option>
                        <option value="Retail">Retail</option>
                        <option value="Service">Service</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Other">Other</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Description</Field.Label>
                    <Input size="sm" {...register('description')} />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                <Button
                  type="submit"
                  colorPalette="brand"
                  loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
                >
                  {isEdit ? 'Save' : 'Create'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
